import { useState, useRef, useEffect, useMemo, useCallback, ReactElement } from 'react';
import ReactDOM from 'react-dom';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { generateUuid } from '@lib/uuid';
import { Icon } from '@nx-frontend/shared/ui/icon';
import { UiLabel } from '@nx-frontend/shared/ui/label';

export interface UiSelectItem<Value = string> {
  value: Value;
  label: string;
  content?: ReactElement;
  hidden?: boolean;
}

interface UiSelectProps<Value = string> {
  onChange: (value: Value) => void;
  options: UiSelectItem<Value>[];
  id?: string;
  name?: string;
  value?: Value;
  label?: string;
  labelClassName?: string;
  padding?: string;
  nullable?: boolean;
  placeholder?: string;
  placeholderClassName?: string;
  children?: ReactElement;
  className?: string[];
  dropdownItemClassName?: string[];
  dropdownClassName?: string[];
  disabled?: boolean;
  loading?: boolean;
  maxWidth?: string;
  dropdownOffsetY?: number;
}

export const UiSelect = <Value,>({
  id: propsId,
  name: propsName,
  onChange,
  options,
  value,
  label,
  labelClassName,
  padding,
  nullable,
  placeholder,
  placeholderClassName,
  children,
  className,
  dropdownItemClassName,
  dropdownClassName,
  disabled = false,
  loading = false,
  maxWidth = '400px',
  dropdownOffsetY,
}: UiSelectProps<Value>) => {
  const t = useTranslations('shared');
  const id = useMemo(() => {
    return propsId != null ? propsId : generateUuid();
  }, [propsId]);
  const name = useMemo(() => {
    return propsName != null ? propsName : generateUuid();
  }, [propsName]);

  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const [currentSelect, setCurrentSelect] = useState<string | null>(() => {
    if (nullable) {
      return null;
    } else {
      return options.length > 0 ? options[0].label : t('select.empty');
    }
  });
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownWidth, setDropdownWidth] = useState('0px');

  const labelRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const selectedOption = options.find((option) => option.value === value);
      const label = selectedOption ? selectedOption.label : loading ? t('select.loading') : t('select.empty');
      setCurrentSelect(label);
    }
  }, [value, options, loading, t]);

  const dropdownHeight = dropdownRef.current?.clientHeight ?? 0;

  const getDropdownPosition = useCallback(() => {
    if (!labelRef.current) return { top: 0, left: 0 };

    const windowHeight = window.innerHeight;
    const rect = labelRef.current.getBoundingClientRect();
    const clickPositionTop = rect.top;
    const spaceBelow = windowHeight - clickPositionTop - rect.height;
    let positionTop;

    if (spaceBelow > dropdownHeight + 20) {
      positionTop = clickPositionTop + rect.height + (dropdownOffsetY ?? 0);
    } else {
      positionTop = clickPositionTop - dropdownHeight - 5;
    }

    return { top: positionTop, left: rect.left };
  }, [dropdownHeight, dropdownOffsetY]);

  useEffect(() => {
    if (showDropdown) {
      setDropdownPosition(getDropdownPosition());
      setDropdownWidth(getDropdownWidth());
      setTimeout(() => {
        setDropdownVisible(true);
      }, 50);
    } else {
      setDropdownVisible(false);
    }
  }, [showDropdown, dropdownRef, getDropdownPosition]);

  const toggleListVisible = () => {
    if (disabled || loading) {
      return setShowDropdown(false);
    }
    setShowDropdown(!showDropdown);
  };

  const handleSelect = (selectedValue: Value, selectedLabel: string) => {
    setCurrentSelect(selectedLabel);
    onChange(selectedValue);
    setShowDropdown(false);
  };

  const getDropdownWidth = () => {
    if (!labelRef.current) return '0px';

    const rect = labelRef.current.getBoundingClientRect();
    return `${rect.width}px`;
  };

  return (
    <div
      className={classNames('relative block min-w-[110px] w-full max-w-[' + maxWidth + ']', className)}
      data-cy="ui-select"
    >
      {label && <UiLabel title={label} type="small" className={labelClassName} />}
      <input id={id} className="select__input" type="hidden" name={name} disabled={disabled || loading} />
      <div
        className={classNames(
          'flex justify-between items-center w-full text-sm border border-1 border-solid rounded-md border-gray-200',
          {
            'py-2.5 px-2': !padding,
            'bg-neutral-100 text-neutral-500 select-none': disabled,
            'bg-white cursor-pointer hover:border-gray-300': !disabled,
          },
          padding
        )}
        onClick={toggleListVisible}
        ref={labelRef}
      >
        {!children && !currentSelect && (
          <div className={classNames('pt-0.5 text-neutral-400', placeholderClassName)}>
            {loading ? t('select.loading') : placeholder ?? t('select.placeholder')}
          </div>
        )}
        {(children || currentSelect) && (
          <div className="pt-0.5 w-full whitespace-nowrap overflow-hidden text-ellipsis">
            {children ?? currentSelect}
          </div>
        )}
        {loading ? (
          <Icon icon="LOADER" className="size-4 my-0.5 animate-spin" />
        ) : (
          <Icon icon="ARROW_4_DOWN" className="size-4 shrink-0 ml-1 " />
        )}
      </div>
      {showDropdown &&
        ReactDOM.createPortal(
          <>
            <div className="absolute w-screen h-screen top-0 left-0 z-[55]" onClick={toggleListVisible}></div>
            <div
              className={classNames(
                dropdownClassName,
                'absolute bg-white shadow-md rounded-md max-h-[205px] overflow-hidden overflow-y-auto z-[56]',
                {
                  'opacity-0': !dropdownVisible,
                }
              )}
              style={{ left: dropdownPosition.left, top: dropdownPosition.top, width: dropdownWidth }}
              ref={dropdownRef}
            >
              <ul>
                {options
                  .filter(({ hidden }) => !hidden)
                  .map((option) => {
                    const isSelected = option.value === value;
                    return (
                      option.value !== 'DEFAULT' && (
                        <li
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleSelect(option.value, option.label);
                          }}
                          key={String(option.value)}
                          className={classNames(
                            dropdownItemClassName,
                            `border-t border-opacity-50 border-gray-300 py-2 px-4 text-sm cursor-pointer list-none transition-colors ${
                              isSelected ? 'bg-gray-100' : 'hover:bg-gray-100'
                            }`
                          )}
                        >
                          {!option.content && option.label}
                          {option.content && option.content}
                        </li>
                      )
                    );
                  })}
              </ul>
            </div>
          </>,
          document.getElementById('select-root') as Element
        )}
    </div>
  );
};
